import { Pill, Stat } from '@salonomy/ui';
import { useCommsCostReport, type ReportRange } from '../../data/reports.js';
import { exportCsvFilename } from '../../lib/csv.js';
import { money, moneyWhole, pluralize } from '../../lib/format.js';
import { ReportCard, type CsvExport } from './shared.js';

/**
 * WS9 — the comms invoice: messaging cost by channel & day plus smart-routing savings. MANAGER-ONLY on the
 * API, so the caller passes the isFloorManager gate as `enabled`; a non-manager never fires a 403-guaranteed
 * read and instead gets an honest note. Every cost is server-computed (ADR-0009) and rendered verbatim.
 */

const CHANNEL_LABEL: Record<string, string> = {
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  push: 'Push',
};

export function CommsCostCard({ range, enabled }: { range: ReportRange; enabled: boolean }) {
  const { data: report, isLoading, isError } = useCommsCostReport(range, enabled);

  if (!enabled) {
    return (
      <ReportCard
        title="Comms cost"
        subtitle="The salon’s messaging invoice and smart-routing savings."
        isEmpty
        emptyLabel="Comms cost is available to salon owners and managers."
      />
    );
  }

  const isEmpty = !!report && report.byChannel.length === 0;

  const csv: CsvExport | null =
    report && report.byChannel.length > 0
      ? {
          filename: exportCsvFilename('comms-cost'),
          headers: ['Channel', 'Messages', 'Segments', 'Cost', 'Smart-routing saved'],
          rows: report.byChannel.map((c) => [
            CHANNEL_LABEL[c.channel] ?? c.channel,
            c.messages,
            c.segments,
            money(c.costPence),
            money(c.savedPence),
          ]),
        }
      : null;

  return (
    <ReportCard
      title="Comms cost"
      subtitle="Messaging cost by channel over this period, with smart-routing savings. Manager-only — server-computed."
      isLoading={isLoading}
      isError={isError}
      isEmpty={isEmpty}
      emptyLabel="No messages sent in this period."
      csv={csv}
    >
      {report ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            <Stat label="Total cost" value={moneyWhole(report.totalCostPence)} />
            <Stat label="Messages" value={report.totalMessages.toLocaleString('en-GB')} />
            {report.smartRoutingSavedPence > 0 ? (
              <Pill tone="soft">{money(report.smartRoutingSavedPence)} saved by smart routing</Pill>
            ) : null}
          </div>

          <div className="settings-list">
            {report.byChannel.map((c) => (
              <div key={c.channel} className="settings-row">
                <div>
                  <div style={{ fontWeight: 600 }}>{CHANNEL_LABEL[c.channel] ?? c.channel}</div>
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    {pluralize(c.messages, 'message')} · {pluralize(c.segments, 'segment')}
                    {c.savedPence > 0 ? ` · ${money(c.savedPence)} saved` : ''}
                  </div>
                </div>
                <Stat label="Cost" value={money(c.costPence)} />
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </ReportCard>
  );
}
